import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import "./basicinfo.scss";

// Forms
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import SignupSchema from "./validations/registerValidation";

// Bootstrap
import Form from "react-bootstrap/Form";
import { Col, Row } from "react-bootstrap";

// API
import { registerUser, registerPatient } from "../../api/auth";
import { sendConfirmEmail } from "../../api/helpReq";
import {
    createGoals,
    getProviderTypes,
    getTherapistTypes,
} from "../../api/TBIRequests";

// Components
import Sallie from "../UIComponents/Sallie";
import PrivacyPage from "./PrivacyPage";
import PrivacyPagePatient from "./PrivacyPagePatient";
import LinkButton from "../UIComponents/LinkButton";
import LabelledInput from "../UIComponents/LabelledInput";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";
import Dropdown from "react-multilevel-dropdown";
import referralList from "./ReferralList";
import PolicyComponent from "./PolicyComponent";
import useListSelector from "../../lib/useListSelector";
import StrokeListSelecter from "../UIComponents/StrokeListSelecter";

// Translation
import { useTranslation } from "../../i18n/TranslationProvider";

const BasicInfo = ({ setOnboardingPercent, setUser, setPatient }) => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [viewPolicy, setViewPolicy] = useState(false);
    const [viewPolicyPatient, setViewPolicyPatient] = useState(false);
    const [alertPolicy, setAlertPolicy] = useState(true);
    const [messagePolicy, setMessagePolicy] = useState(true);
    const [selectReferral, setSelectReferral] = useState("none");
    const [providerTypes, setProviderTypes] = useState(null);
    const [therapistTypes, setTherapistTypes] = useState(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [readAlert, setReadAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [registrationType, setRegistrationType] = useState("");
    const [agreed, setAgreed] = useState(false);

    const { register, handleSubmit, errors, setError } = useForm({
        resolver: yupResolver(SignupSchema),
    });

    const { t } = useTranslation();

    const handleRegistrationTypeChange = (event) => {
        setRegistrationType(event.target.value);
    };
    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);
    };
    const handleLastNameChange = (event) => {
        setLastName(event.target.value);
    };

    const types = [
        {
            category: "TBI/Concussion",
            items: [
                t("basicinfo.primary_diagnosis.tbi.acute"),
                t("basicinfo.primary_diagnosis.tbi.chronic"),
                t("basicinfo.primary_diagnosis.tbi.pcs"),
            ],
        },
        {
            category: "Dementia",
            items: [
                t("basicinfo.primary_diagnosis.dementia.alzheimers"),
                t("basicinfo.primary_diagnosis.dementia.lewy"),
                t("basicinfo.primary_diagnosis.dementia.parkinsons"),
            ],
        },
        {
            category: "Stroke",
            items: [
                t("basicinfo.primary_diagnosis.stroke.brainstem"),
                t("basicinfo.primary_diagnosis.stroke.embolic"),
                t("basicinfo.primary_diagnosis.stroke.hemorrhagic"),
                t("basicinfo.primary_diagnosis.stroke.thrombotic"),
                t("basicinfo.primary_diagnosis.stroke.tia"),
                t("basicinfo.primary_diagnosis.stroke.unknown"),
            ],
        },
        {
            category: "Other",
            items: [
                t("basicinfo.primary_diagnosis.other.abi"),
                t("basicinfo.primary_diagnosis.other.autism"),
                t("basicinfo.primary_diagnosis.other.covid"),
                t("basicinfo.primary_diagnosis.other.epilepsy"),
            ],
        },
    ];

    const [returnedData, setReturnedData] = useState([]);
    const {
        toggleFromSelectedList,
        selectForTherapist,
        addOther,
        selectedData,
        initialize,
    } = useListSelector({
        setInitialData: setReturnedData,
        initialData: returnedData,
    });

    useEffect(() => {
        setReturnedData(types);
        initialize(types);
    }, [t]);

    useEffect(() => {
        const basicInfoFormFill = async () => {
            try {
                getProviderTypes().then((res) => {
                    const { data } = res;
                    setProviderTypes(data);
                });

                getTherapistTypes().then((res) => {
                    const { data } = res;
                    setTherapistTypes(data);
                });
            } catch (err) {
                console.error(err);
                history.push("/oops");
            }
        };

        basicInfoFormFill();
        setOnboardingPercent(10);
    }, []);

    const onSubmit = async (data) => {
        console.log("running", data);
        if (!agreed) {
            setReadAlert(true);
            return;
        }
        let patientType;
        let patientSubType;
        if (data.userType == "patient") {
            if (
                selectedData["TBI/Concussion"] &&
                selectedData["TBI/Concussion"].length === 0 &&
                selectedData["Dementia"].length === 0 &&
                selectedData["Stroke"].length === 0 &&
                selectedData["Other"].length === 0
            ) {
                setErrorMessage(true);
                return;
            }
            patientType = Object.keys(selectedData)[0];
            patientSubType = Object.values(selectedData)[0][0];
            data.patientType = patientType;
            data.patientSubType = patientSubType;
        }

        try {
            setIsLoading(true);
            if (data.userType === "provider" || data.userType === "Therapist") {
                data.messagePolicy = messagePolicy;
                data.alertPolicy = alertPolicy;
            }

            if (selectReferral) {
                data.referral = selectReferral;
            }

            const registerUserResponse = await registerUser(data);
            const responseUserData = registerUserResponse.data;
            setUser({ ...responseUserData, patientSubType, patientType });
            const { firstName, lastName, email, userType } = responseUserData;
            await sendConfirmEmail({
                email: email,
                name: firstName + " " + lastName,
            });

            if (userType === "caregiver") {
                history.push("/RegisterPatient", {
                    fromPatientControl: false,
                });
            } else if (userType === "Therapist") {
                history.push("/notificationPrefs", {
                    fromPatientControl: false,
                });
            } else if (userType === "provider") {
                history.push("/additionalInfo");
            } else {
                // userType is Patient
                data.relationshipType = "self";
                const registerPatientResponse = await registerPatient(data);
                const responsePatientData = registerPatientResponse.data;
                setPatient(responsePatientData);
                await createGoals({ patientId: responsePatientData.patientId });
                if (patientType === "TBI/Concussion") {
                    history.push("/RecentTBI1", {
                        fromPatientControl: false,
                    });
                } else if (patientType === "Stroke") {
                    history.push("/RecentStroke1", {
                        fromPatientControl: false,
                    });
                } else if (patientType === "SMI") {
                    history.push("/RecentSMI1", {
                        fromPatientControl: false,
                    });
                } else {
                    history.push("/RecentCondition1", {
                        fromPatientControl: false,
                    });
                }
            }
        } catch (err) {
            console.error(err);
            if (err.message && err.message.includes("409")) {
                setError("email", {
                    type: "emailTaken",
                    message: t("basicinfo.error.email_taken"),
                });
            } else if (err.message && err.message.includes("406")) {
                setError("email", {
                    type: "emailNotValid",
                    message: t("basicinfo.error.email_invalid"),
                });
            } else {
                history.push("/oops");
            }
        } finally {
            setIsLoading(false);
        }
    };

    let SallieMsg = t("basicinfo.sallie");

    if (
        errors.dob &&
        errors.dob.message &&
        errors.dob.message === "You must be 13 or older"
    ) {
        SallieMsg = t("basicinfo.under13");
    }

    return (
        <>
            <div style={{ display: viewPolicy ? "block" : "none" }}>
                <PrivacyPage
                    firstName={firstName}
                    lastName={lastName}
                    goback={() => {
                        setViewPolicy(false);
                    }}
                    onClick={() => {
                        setAgreed(true);
                        setViewPolicy(false);
                    }}
                />
            </div>
            <div style={{ display: viewPolicyPatient ? "block" : "none" }}>
                <PrivacyPagePatient
                    firstName={firstName}
                    lastName={lastName}
                    goback={() => {
                        setViewPolicyPatient(false);
                    }}
                    onClick={() => {
                        setAgreed(true);
                        setViewPolicyPatient(false);
                    }}
                />
            </div>
            <div
                style={{
                    display: viewPolicy || viewPolicyPatient ? "none" : "block",
                }}
            >
                <OnboardingStepHeader text={t("basicinfo.step")} />
                <Sallie text={SallieMsg} />
                <Form
                    autoComplete="on"
                    action="/post"
                    method="POST"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Form.Row>
                        <Col md={12}>
                            <Form.Group controlId="userType">
                                <Form.Group
                                    as={Row}
                                    className="registration-type"
                                >
                                    <Form.Label
                                        column
                                        className="registration-type-label"
                                    >
                                        {t("basicinfo.registration_type.label")}
                                        <span className="required">*</span>
                                    </Form.Label>
                                    <Col sm={12}>
                                        <Row className="registration-type-radio-row">
                                            <Col sm={2}>
                                                <Form.Check
                                                    type="radio"
                                                    label={t("basicinfo.registration_type.patient")}
                                                    name="userType"
                                                    value="patient"
                                                    checked={registrationType === "patient"}
                                                    onChange={handleRegistrationTypeChange}
                                                    id="registrationTypePatient"
                                                    className="registration-type-radio"
                                                    ref={register}
                                                />
                                            </Col>
                                            <Col sm={2}>
                                                <Form.Check
                                                    type="radio"
                                                    label={t("basicinfo.registration_type.caregiver")}
                                                    name="userType"
                                                    value="caregiver"
                                                    checked={registrationType === "caregiver"}
                                                    onChange={handleRegistrationTypeChange}
                                                    id="registrationTypeCaregiver"
                                                    className="registration-type-radio"
                                                    ref={register}
                                                />
                                            </Col>
                                            <Col sm={4}>
                                                <Form.Check
                                                    type="radio"
                                                    label={t("basicinfo.registration_type.provider")}
                                                    name="userType"
                                                    value="provider"
                                                    checked={registrationType === "provider"}
                                                    onChange={handleRegistrationTypeChange}
                                                    id="registrationTypeProvider"
                                                    className="registration-type-radio"
                                                    ref={register}
                                                />
                                            </Col>
                                            <Col sm={4}>
                                                <Form.Check
                                                    type="radio"
                                                    label={t("basicinfo.registration_type.therapist")}
                                                    name="userType"
                                                    value="Therapist"
                                                    checked={registrationType === "Therapist"}
                                                    onChange={handleRegistrationTypeChange}
                                                    id="registrationTypeTherapist"
                                                    className="registration-type-radio"
                                                    ref={register}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Form.Group>
                                {errors.userType && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.userType.message}
                                    </Form.Control.Feedback>
                                )}
                                {registrationType === "patient" && (
                                    <>
                                        <h6>
                                            {t("basicinfo.primary_diagnosis.label")}
                                            <span className="required">*</span>
                                        </h6>
                                        <Col md={12} className="mt-3">
                                            <div className="flex flex-col md:flex-row">
                                                {returnedData.map((itemList, i) => {
                                                    const { category } = itemList;
                                                    const categoryKeyMap = {
                                                        "TBI/Concussion": "tbi",
                                                        "Dementia": "dementia",
                                                        "Stroke": "stroke",
                                                        "Other": "other",
                                                    };
                                                    const categoryKey = categoryKeyMap[category] || category.toLowerCase();
                                                    return (
                                                        <StrokeListSelecter
                                                            key={category}
                                                            category={category}
                                                            message={
                                                                t(`basicinfo.primary_diagnosis.${categoryKey}.category`)
                                                            }
                                                            list={itemList.items}
                                                            index={i}
                                                            isTherapist={true}
                                                            selectedList={selectedData[category]}
                                                            toggleFromSelectedList={toggleFromSelectedList}
                                                            selectForTherapist={selectForTherapist}
                                                            addOther={addOther}
                                                            footerItem={
                                                                category === "Dementia"
                                                                    ? t("basicinfo.primary_diagnosis.dementia.smi")
                                                                    : undefined
                                                            }
                                                            footerHeader={
                                                                category === "Dementia"
                                                                  ? t("basicinfo.primary_diagnosis.smi.category")
                                                                  : undefined
                                                            }
                                                            footerCategory={
                                                                category === "Dementia" ? "SMI" : undefined
                                                            }
                                                            footerSelectedList={
                                                                category === "Dementia"
                                                                    ? selectedData["SMI"]
                                                                    : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </Col>
                                    </>
                                )}
                                {registrationType === "provider" && (
                                    <>
                                        <Form.Group controlId="providerType">
                                            <Form.Label>
                                                {t("basicinfo.provider_type.label")}{" "}
                                                <span className="required">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                autoFocus={true}
                                                name="providerType"
                                                as="select"
                                                ref={register}
                                                isInvalid={errors.providerType}
                                            >
                                                {providerTypes
                                                    ? providerTypes.map((providerType) => (
                                                          <option
                                                              key={providerType.constantString}
                                                              value={providerType.constantString}
                                                          >
                                                              {t(`basicinfo.provider_type.${providerType.constantString}`) || providerType.displayString}
                                                          </option>
                                                      ))
                                                    : null}
                                            </Form.Control>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.providerType && errors.providerType.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </>
                                )}
                                {registrationType === "Therapist" && (
                                    <Form.Group controlId="therapistType">
                                        <Form.Label>
                                            {t("basicinfo.therapist_type.label")}{" "}
                                            <span className="required">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            name="therapistType"
                                            as="select"
                                            ref={register}
                                            isInvalid={!!errors.therapistType}
                                        >
                                            {therapistTypes?.map((type) => (
                                                <option
                                                    key={type.constantString}
                                                    value={type.constantString}
                                                >
                                                    {t(`basicinfo.therapist_type.${type.constantString}`) || type.displayString}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.therapistType?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                                {errorMessage && (
                                    <Form.Control.Feedback
                                        style={{ display: "block" }}
                                        type="invalid"
                                    >
                                        {t("basicinfo.error.select_diagnosis")}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <LabelledInput
                                name="firstName"
                                required
                                label={t("basicinfo.name.first")}
                                autoComplete="given-name"
                                onChange={handleFirstNameChange}
                                inputRef={register}
                                errors={errors}
                            />
                        </Col>

                        <Col md={3}>
                            <LabelledInput
                                name="lastName"
                                autoComplete="family-name"
                                label={t("basicinfo.name.last")}
                                onChange={handleLastNameChange}
                                required
                                inputRef={register}
                                errors={errors}
                            />
                        </Col>

                        <Col md={6}>
                            <LabelledInput
                                name="email"
                                type="email"
                                label={t("basicinfo.email")}
                                autoComplete="username"
                                required
                                inputRef={register}
                                errors={errors}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="dob">
                                <Form.Label>
                                    {t("basicinfo.dob")}
                                    <span className="required">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="dob"
                                    ref={register}
                                    isInvalid={!!errors.dob}
                                    placeholder="MM/DD/YYYY"
                                    max={new Date().toISOString().split('T')[0]}
                                />
                                {errors.dob && (
                                    <Form.Control.Feedback
                                        type="invalid"
                                        style={{ display: "block" }}
                                    >
                                        {errors.dob.message}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="gender">
                                <Form.Label>
                                    {t("basicinfo.gender.label")}
                                    <span className="required">*</span>
                                </Form.Label>
                                <Form.Control
                                    name="gender"
                                    as="select"
                                    ref={register}
                                    isInvalid={errors.gender}
                                >
                                    <option value=""></option>
                                    <option value="male">
                                        {t("basicinfo.gender.male")}
                                    </option>
                                    <option value="female">
                                        {t("basicinfo.gender.female")}
                                    </option>
                                    <option value="other">
                                        {t("basicinfo.gender.other")}
                                    </option>
                                </Form.Control>
                                {t("basicinfo.gender.label") !== "Género" && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.gender && errors.gender.message}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <LabelledInput
                                name="postalCode"
                                autoComplete="postal-code"
                                label={t("basicinfo.zipcode")}
                                required
                                inputRef={register}
                                type="text"
                                errors={errors}
                            />
                        </Col>

                        <Col md={6}>
                            <Row className="d-flex md:mt-[1.8rem]">
                                <Dropdown
                                    position="right"
                                    title={referralList.name}
                                    menuClassName="text-14 px-2 border-b-1 border-solid border-blue hover:border-black"
                                    style={{
                                        marginLeft: "1.3rem",
                                        fontWeight: "normal",
                                        fontSize: "15px",
                                        border: "1px solid #d4d4d4",
                                    }}
                                >
                                    {referralList.children &&
                                        referralList.children.map((item, i) => (
                                            <Dropdown.Item
                                                key={i}
                                                onClick={() => setSelectReferral(item)}
                                            >
                                                {item}
                                            </Dropdown.Item>
                                        ))}
                                </Dropdown>
                                <span className="required">*</span>
                                <div
                                    style={{
                                        margin: "0 1rem 1rem 0.5rem",
                                        minHeight: "2.3rem",
                                        width: "50%",
                                        lineHeight: "2.3rem",
                                        fontWeight: "normal",
                                        fontSize: "15px",
                                        border: "1px solid #d4d4d4",
                                        borderRadius: "3px",
                                        backgroundColor: "white",
                                        alignItems: "center",
                                        textAlign: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {selectReferral}
                                </div>
                            </Row>
                        </Col>

                        <Col md={6}>
                            <LabelledInput
                                name="password"
                                type="password"
                                label={t("basicinfo.password")}
                                required
                                autoComplete="new-password"
                                inputRef={register}
                                errors={errors}
                            />
                        </Col>
                        <Col md={6}>
                            <LabelledInput
                                type="password"
                                name="passwordConfirmation"
                                label={t("basicinfo.confirm_password")}
                                autoComplete="new-password"
                                required
                                inputRef={register}
                                errors={errors}
                            />
                        </Col>

                        {(registrationType === "Therapist" ||
                            registrationType === "provider") && (
                            <>
                                <PolicyComponent
                                    label={t("basicinfo.messages")}
                                    radioName="radio-type-1"
                                    idYes="field-yes-1"
                                    idNo="field-no-1"
                                    policyValue={messagePolicy}
                                    setPolicyValue={setMessagePolicy}
                                />
                                <PolicyComponent
                                    label={t("basicinfo.alerts")}
                                    radioName="radio-type-2"
                                    idYes="field-yes-2"
                                    idNo="field-no-2"
                                    policyValue={alertPolicy}
                                    setPolicyValue={setAlertPolicy}
                                />
                                <Col md={12} className="terms-and-conditions">
                                    <div>
                                        <span>{t("basicinfo.terms.accept")} </span>
                                        <LinkButton
                                            type="button"
                                            onClick={() => setViewPolicy(true)}
                                        >
                                            <h5>{t("basicinfo.terms.link")}</h5>
                                        </LinkButton>
                                    </div>
                                    {readAlert && !agreed && (
                                        <Form.Control.Feedback
                                            style={{ display: "block" }}
                                            type="invalid"
                                        >
                                            {t("basicinfo.error.read_terms")}
                                        </Form.Control.Feedback>
                                    )}
                                </Col>
                            </>
                        )}

                        {(registrationType === "patient" ||
                            registrationType === "caregiver") && (
                            <Col md={12} className="terms-and-conditions">
                                <div>
                                    <span>{t("basicinfo.terms.accept")} </span>
                                    <LinkButton
                                        type="button"
                                        onClick={() => setViewPolicyPatient(true)}
                                    >
                                        <h5>{t("basicinfo.terms.link")}</h5>
                                    </LinkButton>
                                </div>
                                {readAlert && !agreed && (
                                    <Form.Control.Feedback
                                        style={{ display: "block" }}
                                        type="invalid"
                                    >
                                        {t("basicinfo.error.read_terms")}
                                    </Form.Control.Feedback>
                                )}
                            </Col>
                        )}

                        <Col md={12} className="mt-2">
                            <SubmitButton disabled={isLoading} type="submit">
                                {`${t("onboarding.saveContinue")} >`}
                            </SubmitButton>
                        </Col>
                    </Form.Row>
                </Form>
            </div>
        </>
    );
};

export default BasicInfo;